import type { ReactNode } from 'react';
import { Band } from './Band';

export interface PageHeaderProps {
  /** Sentence-case kicker, usually a link with the `eyebrow` class. */
  eyebrow: ReactNode;
  /** The page's one h1. The caller paints its own `.muted-word`. */
  title: ReactNode;
  /** The single sentence under the title. */
  lede?: ReactNode;
  /** A platform or provider mark, set beside the title. */
  mark?: ReactNode;
  /** `center` for the pages whose header has no body copy beside it. */
  align?: 'left' | 'center';
  /** Passed to the `Band`, for headers that open on the wash. */
  wash?: 'wash-down' | 'wash-up' | 'wash';
  /** The row under the lede: a search box, a call to action pair, a fact strip. */
  children?: ReactNode;
}

/**
 * The first `Band` of an editorial page: eyebrow, h1, lede, and whatever the
 * page puts under them. One component so every page's opening has the same
 * rhythm and the h1 is the same object on all of them.
 */
export const PageHeader = ({
  eyebrow,
  title,
  lede,
  mark,
  align = 'left',
  wash,
  children,
}: PageHeaderProps) => {
  const centered = align === 'center';
  return (
    <Band wash={wash} className={centered ? 'text-center' : ''}>
      <div className="band">
        {eyebrow}
        <div className={`flex items-center gap-4 ${centered ? 'justify-center' : ''}`}>
          {mark}
          <h1 className="display-2 text-text">{title}</h1>
        </div>
        {lede ? <p className={`lede mt-5 max-w-190 ${centered ? 'mx-auto' : ''}`}>{lede}</p> : null}
        {children ? <div className="mt-8">{children}</div> : null}
      </div>
    </Band>
  );
};
